// Dashboard — post-onboarding home (W36.0, per docs/spec/dashboard/README.md).
//
// This route has **two** implementations behind one `useIsMobile()` fork:
//
//   mobile  — the W36.0 rebuild: today's state + cross-module aggregation
//             (§3.1 fixed order; W57.0 re-ordered it, see below)
//   desktop — the pre-W36.0 page, unchanged
//
// Spec §2.1 Q4 locks this split ("本輪僅移動端; 桌面端維持現狀"). The desktop
// branch lives in `./dashboard/DesktopDashboard` and is byte-for-byte the old
// page — it was moved out only because this file crossed the AGENTS.md §3 #10
// 15 KB route ceiling once the mobile branch landed beside it.
//
// The mobile branch waits on exactly ONE request (`useDashboard`, spec §5.1).
// It does not fan out into per-module queries — that was the old page's shape
// and it is what the aggregation endpoint replaced.
//
// ## W57.0 — the block order
//
// Before:  Hero → 天氣 → 紀念日 → 心情 → 動態
// After:   Hero → 天氣 → 心情(兩張小卡) → 紀念日 → 動態
//
// The mood pair moved **up**, under the weather card, for two reasons. It is
// the most likely thing a person opens the app to do (a daily check-in), and a
// row of two small cards is the page's only break from the stack of wide
// rectangles — putting it second makes that break land where the eye arrives
// first rather than below three full-width blocks.
//
// The spec's §3.1 order was updated in the same change
// (`docs/spec/dashboard/README.md`), and the reasoning is in
// `docs/spec/w57.0-dashboard-polish.md` §3.

import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { InfoBanner } from "@/components/primitives/InfoBanner";
import { LoadingState } from "@/components/primitives/LoadingState";
import { ErrorState } from "@/components/primitives/ErrorState";
import { PullToRefreshBar } from "@/components/patterns/PullToRefreshBar";
import { PageContainer, SectionStack } from "@/components/layout";
import { AccentCard } from "@/components/primitives/HeroCard";
import { ACCENT_DASHBOARD } from "@/lib/screenType";
import { BirthdayBackfillCta } from "@/components/profile/BirthdayBackfillCta";
import { useAuth } from "@/lib/auth/AuthProvider";
import {
  useClearDailyMessage,
  useDashboard,
  useMarkDailyMessageRead,
  useSaveDailyMessage,
  useSaveMoodCheckIn,
} from "@/hooks/useDashboard";
import { useIsMobile } from "@/lib/hooks/useIsMobile";
import { DASHBOARD_I18N } from "@/lib/i18n/dashboard";
import { formatAbsoluteZh } from "@/lib/utils/date";

import { DesktopDashboard } from "./dashboard/DesktopDashboard";
import { DashboardHero } from "./dashboard/components/DashboardHero";
import { PairHeroCard } from "./dashboard/components/PairHeroCard";
import { DailyAnniversaryCard } from "./dashboard/components/DailyAnniversaryCard";
import { MoodPair } from "./dashboard/components/MoodPair";
import { ActivityFeed } from "./dashboard/components/ActivityFeed";
import { MessageEditorSheet } from "./dashboard/components/MessageEditorSheet";
import { MoodPickerSheet } from "./dashboard/components/MoodPickerSheet";
import { WeatherCard } from "./dashboard/components/WeatherCard";
import { DashboardCreateAction } from "./dashboard/components/CreateSheet";
import { resolvePairSides } from "./dashboard/components/dashboardHelpers";
import { useWeatherLocation } from "@/hooks/useWeatherLocation";

export function DashboardPage() {
  const isMobile = useIsMobile();
  return isMobile ? <MobileDashboard /> : <DesktopDashboard />;
}

// ---------------------------------------------------------------------------
// Mobile — W36.0 rebuild, W57.0 re-ordered
// ---------------------------------------------------------------------------

function MobileDashboard() {
  const { user } = useAuth();
const nav = useNavigate();
  // W47.1: the coordinates are resolved **before** the request rather than after
  // it. Asking afterwards would mean the first paint always shows the fallback
  // and then silently swaps cities under the user — which is the one thing a card
  // labelled 「未授權 · 顯示香港」 must never do. The permission lifecycle lives in
  // `useWeatherLocation`, which `AGENTS.md §3 #10` pushed out of this route.
  //
  // A grant needs no explicit refetch: `useDashboard`'s query key already carries
  // the coordinates, so new coordinates are a new key and React Query fetches.
  const {
    coords,
    reason: locationReason,
    requesting: requestingLocation,
    ask: askForLocation,
  } = useWeatherLocation();
  const { data, isLoading, isError, noPair, refetch } = useDashboard(coords);

  const [messageOpen, setMessageOpen] = useState(false);
  const [moodOpen, setMoodOpen] = useState(false);

  // `data.date` is HKT from the server, not the browser clock — a user in a
  // different timezone must see the pair's "today", not theirs.
  const today = useMemo(() => new Date(`${data?.date ?? ""}T00:00:00`), [data?.date]);
  const dateLabel = data?.date
    ? formatAbsoluteZh(today)
    : formatAbsoluteZh(new Date());

  /**
   * W57.0 — **who is who, relative to the viewer**.
   *
   * The payload's `pair.members` is ordered `(owner, partner)`, and the owner is
   * a property of the pair rather than of the session — so for half the pair,
   * `members[0]` is the *other* person. Every card that shows two people asks
   * this resolver instead of indexing the array; see `dashboardHelpers.ts`.
   */
  const currentUserId = user?.id ?? null;
  const sides = useMemo(
    () => resolvePairSides(data?.pair.members ?? [], currentUserId),
    [data?.pair.members, currentUserId],
  );

  const saveMessage = useSaveDailyMessage(data?.date ?? "");
  const clearMessage = useClearDailyMessage(data?.date ?? "");
  const markRead = useMarkDailyMessageRead(data?.date ?? "");
  const saveMood = useSaveMoodCheckIn(data?.date ?? "");

  // Opening the editor is what marks an `updated` message read (§3.3): the
  // sheet is only ever opened deliberately by tapping the message block.
  const openMessageSheet = () => {
    setMessageOpen(true);
    if (data?.daily_message.read_state === "updated") {
      // The date is already bound into the mutation (see useDashboard), so this
      // needs no argument.
      void markRead.mutateAsync();
    }
  };

  // --- Not paired: render the prompt and NOTHING else (§3.4) --------------
  if (noPair) {
    return (
      <div data-testid="dashboard-page" className="flex flex-col">
        <DashboardHero dateLabel={dateLabel} />
        <PageContainer vertical="loose">
          <Card>
            <div className="p-s-5 flex flex-col gap-s-4" data-testid="dashboard-no-pair">
              <h2 className="font-display text-title-3 text-ink-1">
                {DASHBOARD_I18N.empty.title}
              </h2>
              <p className="text-body-sm text-ink-2">{DASHBOARD_I18N.empty.description}</p>
              <Link to="/pair" data-testid="link-pair" className="self-start">
                <Button variant="primary">{DASHBOARD_I18N.empty.cta}</Button>
              </Link>
            </div>
          </Card>
        </PageContainer>
      </div>
    );
  }

  return (
    // W47.0: the `pb-32` that used to sit here is gone. `MobileShell` already
    // pads `<main>` by `pb-32` for the pill nav, so the two stacked into 256px of
    // clearance on this route alone — a screen that scrolled further than every
    // other one for no reason. The shell owns it.
    <div data-testid="dashboard-page" className="flex flex-col">
      <DashboardHero
        dateLabel={dateLabel}
        trailing={<DashboardCreateAction />}
      />
      <PageContainer vertical="loose">
        <PullToRefreshBar
          onRefresh={async () => {
            await refetch();
          }}
          data-testid="dashboard-pull-to-refresh"
        >
          <SectionStack density="loose">
            {user && !user.email_verified_at && user.email ? (
              <InfoBanner tone="partner">
                請驗證你的電子郵件，未驗證前部分功能將受限制。
              </InfoBanner>
            ) : null}

            {/* W36.1 (profile spec §3.2): the same backfill CTA the profile
                page shows. It is here because this is where a person lands —
                a CTA they have to go and find is not a CTA. A member without
                a birthday never gets a generated anniversary row, so this is
                the difference between the two sides having a shared calendar
                and one of them being invisible in it. */}
            {user && !user.birthday ? (
              <BirthdayBackfillCta onClick={() => nav("/profile")} />
            ) : null}

            {isLoading ? (
              // First paint must never be a blank screen (§3.4).
              <LoadingState count={4} />
            ) : isError || !data ? (
              <ErrorState
                title={DASHBOARD_I18N.error.title}
                retryLabel={DASHBOARD_I18N.error.retry}
                onRetry={() => void refetch()}
              />
            ) : (
              <>
                {/* W43 `ADR-0109`: the dashboard's card language. The hero is
                    pale and carries the mascot (「我們的空間」 + 在一起 N 天);
                    the two feature cards below it are saturated accents, one
                    each; the module list is white. That is 1 pale + 2 accents on
                    one screen, against W42's 1 saturated and a screen of pale
                    cards.

                    The colours come from `ACCENT_DASHBOARD` by **role**, not by
                    a hard-coded `surface-lime` in JSX — so a palette change lands
                    here without this file becoming the one place that disagrees
                    with the table. */}
                <PairHeroCard
                  pairName={data.pair.name}
                  relationshipStart={data.pair.relationship_start}
                  daysTogether={data.pair.days_together}
                  // W36.1: the hero's two avatars are the pair's real members.
                  // W57.0: and they are resolved against the **viewer**, not
                  // against the payload's owner-first order.
                  members={data.pair.members}
                  currentUserId={currentUserId}
                  message={data.daily_message}
                  onEditMessage={openMessageSheet}
                  messageTotal={data.daily_message.total_count}
                  showPartnerUpdated={data.daily_message.read_state === "updated"}
                />

                {/* W47.1 — second block, directly after the hero. `surface-ice`
                    is the peak tier (9.41:1), NOT the pale `--tint-hero`: that
                    token is 「每屏恰好一張」 and the hero above already spends it.
                    W57.0: the card is now a real `AccentCard`, so the accent
                    table can see it — see `WeatherCard`'s docstring. */}
                {ACCENT_DASHBOARD.weather ? (
                  <AccentCard slot={ACCENT_DASHBOARD.weather} testId="dashboard-weather-accent">
                    <WeatherCard
                      bare
                      weather={data.weather}
                      onRequestLocation={() => void askForLocation()}
                      requestingLocation={requestingLocation}
                      locationReason={locationReason}
                    />
                  </AccentCard>
                ) : (
                  <WeatherCard
                    weather={data.weather}
                    onRequestLocation={() => void askForLocation()}
                    requestingLocation={requestingLocation}
                    locationReason={locationReason}
                  />
                )}

                {/* W57.0 — the mood pair. Two **independent** white cards, one
                    row, one per person. It used to be one saturated card with
                    two columns in it, and at 320px a column was ~85px wide
                    while the line in it was 156px — so 46% of a user-authored
                    sentence was cut (`ADR-0134`). Two cards give each column
                    ~140px, and the saturation goes to the avatars and the
                    emoji instead of two large blocks. */}
                <MoodPair
                  moods={data.moods}
                  activity={data.latest_activity}
                  sides={sides}
                  onPickMood={() => setMoodOpen(true)}
                />

                {ACCENT_DASHBOARD.countdown ? (
                  <AccentCard
                    slot={ACCENT_DASHBOARD.countdown}
                    label="下一個紀念日"
                    testId="dashboard-anniversary-card"
                  >
                    <DailyAnniversaryCard
                      bare
                      anniversary={data.anniversary}
                      daysTogether={data.pair.days_together}
                    />
                  </AccentCard>
                ) : (
                  <DailyAnniversaryCard
                    anniversary={data.anniversary}
                    daysTogether={data.pair.days_together}
                  />
                )}

                {/* White: the module list is content, not a card that earns
                    colour. It is the largest block on the screen and it was
                    reading as one more pale rectangle.

                    W52.1 D-5 — the wrapper `<section>` + its `<h2>` are gone.
                    `ActivityFeed` now renders the shared `ActivityStreamCard`
                    itself, so leaving them would have produced a card inside a
                    card with two headings saying the same thing. The heading
                    copy moves with the shell, not with this call site. */}
                <ActivityFeed
                  items={data.activity_feed}
                  currentUserId={currentUserId}
                  sides={sides}
                />
              </>
            )}
          </SectionStack>
        </PullToRefreshBar>
      </PageContainer>

      <MessageEditorSheet
        open={messageOpen}
        onOpenChange={setMessageOpen}
        initialContent={data?.daily_message.content ?? ""}
        hasExisting={Boolean(data?.daily_message.content)}
        onSave={async (content) => {
          await saveMessage.mutateAsync(content);
        }}
        onClear={async () => {
          await clearMessage.mutateAsync();
        }}
        isSaving={saveMessage.isPending}
        isClearing={clearMessage.isPending}
      />
      <MoodPickerSheet
        open={moodOpen}
        onOpenChange={setMoodOpen}
        currentMood={data?.moods.me ?? null}
        onSelect={async (code) => {
          await saveMood.mutateAsync(code);
        }}
        isSaving={saveMood.isPending}
      />
    </div>
  );
}
